import { Component, inject, Input, OnInit } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { DateHelper, DateTimeUnit } from '../../../helpers/date.helper';
import { BaseModalDialogDirective } from '../../../directives/base-modal-dialog.directive';
import { ModalCloseType } from '../../modal/modal-close-type.enum';

export enum ManualQuarantineType {
  ForTime = 'ForTime',
  UntilDateTime = 'UntilDateTime',
  UntilMidnight = 'UntilMidnight',
  Manual = 'Manual',
}

@Component({
  selector: 'app-quarantine-modal',
  templateUrl: './quarantine-modal.component.html',
  styleUrl: './quarantine-modal.component.scss',
  standalone: false,
})
export class QuarantineModalComponent extends BaseModalDialogDirective implements OnInit {
  private readonly formBuilder = inject(FormBuilder);

  @Input() title: string;
  @Input() entityName: string;
  @Input() isQuarantined = false;
  @Input() endOfQuarantine?: Date;
  @Input() existingReason?: string;

  readonly ManualQuarantineType = ManualQuarantineType;
  readonly DateTimeUnit = DateTimeUnit;

  get closeType(): ModalCloseType {
    return ModalCloseType.Safe;
  }

  form = this.formBuilder.group({
    type: [ManualQuarantineType.Manual],
    duration: [{ value: 3600000, disabled: true }],
    time: [{ value: DateHelper.addTime(new Date(), 1, DateTimeUnit.Hour), disabled: true }],
    reason: ['', Validators.maxLength(500)],
  });

  get f() {
    return this.form.controls;
  }

  constructor() {
    super();
    this.form.controls.type.valueChanges.subscribe((value: ManualQuarantineType) => {
      this.updateFormControls(value);
    });
  }

  ngOnInit(): void {
    if (this.endOfQuarantine != null) {
      this.f.type.setValue(ManualQuarantineType.UntilDateTime);
      this.f.time.setValue(this.endOfQuarantine);
    }
    if (this.isQuarantined) {
      this.f.reason.setValue(this.existingReason);
    }
  }

  private updateFormControls(value: ManualQuarantineType): void {
    switch (value) {
      case ManualQuarantineType.ForTime:
        this.f.duration.enable();
        this.f.time.disable();
        break;
      case ManualQuarantineType.UntilDateTime:
        this.f.duration.disable();
        this.f.time.enable();
        this.f.time.setValue(DateHelper.addTime(new Date(), 1, DateTimeUnit.Hour));
        this.f.time.updateValueAndValidity();
        break;
      case ManualQuarantineType.UntilMidnight:
      case ManualQuarantineType.Manual:
        this.f.duration.disable();
        this.f.time.disable();
        break;
    }
  }

  setQuarantineManually(): void {
    if (this.form.invalid) {
      return;
    }

    let endOfQuarantine: Date = null;
    const type = this.form.controls.type.value;
    switch (type) {
      case ManualQuarantineType.ForTime: {
        const duration = this.form.controls.duration?.value;
        endOfQuarantine = DateHelper.addTime(new Date(), duration, DateTimeUnit.Millisecond);
        break;
      }
      case ManualQuarantineType.UntilDateTime: {
        const time = this.form.controls.time?.value;
        endOfQuarantine = new Date(time);
        break;
      }
      case ManualQuarantineType.UntilMidnight:
        endOfQuarantine = DateHelper.endOfDay(new Date());
        break;
      default:
        endOfQuarantine = null;
        break;
    }

    const reason = this.f.reason.value?.trim() || undefined;
    this.close({ endOfQuarantine, reason });
  }
}
